import { uri } from '../data-util'
import langs from './language.json'
import { html, render } from 'lit/html.js'
import 'view-base/unit-link'
import { title } from './index.js'
import { provider } from './provider'
import { get as val } from 'object-path'
import * as util from '../view-advice-util'


export default function createTable( xeon ) {

  const lang = uri.lang()
  const dict = langs[ lang ]
  const data = provider( xeon )
  // Good working list with all the required info
  const items = data.list( lang )
  // Find factors influencing exposure
  const factors = data.factors( items )

  // ------------------------
  // HEAD
  // ------------------------
  const head = []
  const cols = []
  // Chapter
  cols.push( html `<th></th>` )
  // Exposure
  cols.push( html `<th><span style="font-size:1.2em;">&sum;</span> ${dict.exposure}</th>` )
  for ( const item of Object.values( items ) ) {
    cols.push( html `<th>${item.name.substr(0,3)}</th>` )
  }
  // Finalize
  head.push( html `<tr>${cols}</tr>` )


  // ------------------------
  // BODY
  // ------------------------
  const rows = []

  for ( const item of Object.values( items ) ) {
    const cells = []
    // Substanzname
    cells.push( html `<td>
      <b>${util.capitalize(item.name.substr(0,3))}</b>${item.name.substr(3)}
    </td>` )
    // Sum Exposure
    if ( val( item, "rule.predict" ) ) {
      const ratio = val( item, "rule.predict.auc_ratio", "-" )
      const trend = val( item, "rule.predict.auc_trend" )
      const interval = val( item, "rule.predict.auc_interval" )
      const net = val( item, "rule.predict.net_effect" )
      const res = val( item, "rule.predict.result" )
      const status = val( item, "rule.predict.status" )
      // Find sups
      const cyps = val( item, "rule.predict.auc_factor" ) || {}
      const sups = factors.filter( fac => {
        return typeof cyps[ fac.key.toLowerCase() ] != "undefined"
      } ).map( ( fac, idx ) => {
        var c = []
        if ( idx > 0 ) c.push( html `<sup>,</sup>` )
        // No color for now due to contrast
        if ( fac.val ) c.push( html `<sup>${fac.idx}</sup>` )
        if ( !fac.val ) c.push( html `<sup>${fac.idx}</sup>` )
        return c
      } )
      if ( status == "failure_inval" ) {
        cells.push( html `<td>
              <b>n.a.</b>
            </td>` )
      } else if ( status == "failure_uncle" ) {
        cells.push( html `<td>
              <b>↑↓</b>
            </td>` )
      } else if ( ratio && status == "success_quant" ) {
        if ( !interval || interval.length < 2 ) {
          cells.push( html `<td style=${colorize(ratio)}>
              <b>${ratio}</b>${sups}
            </td>` )
        } else {
          cells.push( html `<td style=${colorize(ratio,interval)}>
              <b>${ratio}</b>
              [${interval.toString()}]
              ${sups}
            </td>` )
        }
      } else {
        cells.push( html `<td>
              <b>${arrow(net)}</b>
            </td>` )
      }
    }
    // Factor Influence
    for ( const item2 of Object.values( items ) ) {
      if ( item.name == item2.name ) {
        cells.push( html `<td></td>` )
      } else {
        // Check for influence of item2 in item
        const factor = item2.rule ?
          val( item, [ "rule", "predict", "success", item2.rule.drug ] ) :
          null
        if ( factor ) {
          cells.push( html `<td style="${colorize(factor.auc_ratio)}">
            ${ factor.auc_ratio || "-" }
          </td>` )
        } else {
          const failure = item2.rule ?
            val( item, [ "rule", "predict", "failure", item2.rule.drug ] ) :
            null
          if ( !failure ) {
            cells.push( html `<td>n.a.</td>` )
          } else {
            const trend = failure.message.split( "," )[ 0 ].slice( 3 )
            cells.push( html `<td>${arrow(trend)}</td>` )
          }
        }
      }
    }
    // Finalize row
    rows.push( html `<tr>${cells}</tr>` )
  }


  // ------------------------------
  // Legende with factors
  // ------------------------------
  const refs = []
  if ( factors.length ) {
    const liste = []
    for ( const fac of factors ) {
      liste.push( liste.length > 0 ? ", " : "" )
      if ( !fac.val ) {
        liste.push( html `<sup style="color:#FFBE5B">${fac.idx}</sup>` )
        liste.push( html `<span style="color:#FFBE5B">${fac.key}</span>` )
      } else {
        liste.push( html `<sup>${fac.idx}</sup>` )
        liste.push( html `<span>${fac.key}${fac.val}</span>` )
      }
    }
    refs.push( html `
      <span>${dict.genotypes} <span>${ liste }<br>
    ` )
  }
  // Legende
  refs.push( html `<span>${dict.legenda} ${dict.legenda_no_change}. ${dict.na}</span><br>` )

  return html `
    <table class="kinetic zahl">
      <thead>${ head }</thead>
      <tbody>${ rows }</tbody>
    </table>
    <div class=caption>${ refs }</div>
    `
}


function colorize( ratio, range ) {

  var value = ratio

  if ( value <= 1.5 && value >= 0.7 && range ) {
    // Maybe to big
    value = Math.max( ratio, range[ 0 ], range[ 1 ] )
    // Maybe to small
    if ( value <= 1.5 ) {
      value = Math.min( ratio, range[ 0 ], range[ 1 ] )
    }
  }

  if ( value > 1.5 ) {
    return `background:rgba(247,103,158,${Math.min(value/10,0.8)});`
  }

  if ( value < 0.7 ) {
    return `background:rgba(247,103,158,${Math.min(value*5,0.8)});`
  }

  return ""
}

function arrow( trend ) {
  if ( !trend || trend == "n.a." ) {
    return "n.a."
  }
  if ( trend.indexOf( "≈" ) > -1 ) {
    return "≈"
  }
  if ( trend.indexOf( "++" ) > -1 ) {
    return trend.replace( "++", "↑↑" )
  }
  if ( trend.indexOf( "+" ) > -1 ) {
    return trend.replace( "+", "↑" )
  }
  if ( trend.indexOf( "--" ) > -1 ) {
    return trend.replace( "--", "↓↓" )
  }
  if ( trend.indexOf( "-" ) > -1 ) {
    return trend.replace( "-", "↓" )
  }
  return "–"
}